import { Component, Input, ViewChild } from '@angular/core';
import { ModalComponent } from 'src/app/shared/modal/modal.component';
import { MailerSubscriptionService } from 'src/app/shared/services/mailer-subscription.service';
import { UsersService } from 'src/app/users/users.service';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';

const NONE_PERIOD = 0;

@Component({
	selector: 'app-site-mailer-subscriptions',
	templateUrl: './site-mailer-subscriptions.component.html',
	styleUrls: ['./site-mailer-subscriptions.component.css']
})
export class SiteMailerSubscriptionsComponent {
	@Input() siteId: number;
	@Input() siteInfo: any = {};

	@ViewChild('mailerSubscriptionsModal') modal: ModalComponent;

	readonly NONE_PERIOD = NONE_PERIOD;
	periodItems: { value: number, labelKey: string }[];

	users: any[] = [];
	selectedUserId: number = null;
	reportRows: { key: string, reports: { code: number, labelKey: string, periods: number[] }[] }[] = [];
	saving = false;

	constructor(
		public mailerSubscriptionService: MailerSubscriptionService,
		public usersService: UsersService,
		private notificationMessage: NotificationMessageService,
	) {
		this.periodItems = [
			{ value: NONE_PERIOD, labelKey: 'site_mailer_subscriptions.period_none' },
			...this.mailerSubscriptionService.PERIOD_OPTIONS,
		];
	}

	open() {
		this.reportRows = this.mailerSubscriptionService.getEligibleReportRows(this.siteInfo)
			.map(family => ({
				key: family.key,
				reports: family.reports.map(report => ({ ...report, periods: [NONE_PERIOD] }))
			}));

		const currentUser = this.usersService.getCurrentUser();
		this.selectedUserId = currentUser?.id || null;

		this.mailerSubscriptionService.getEligibleUsers(this.siteId).subscribe((users: any) => {
			this.users = users.map(user => ({
				...user,
				fullName: `${user.first_name} ${user.last_name}`
			}));

			this.modal.show();

			if (this.selectedUserId)
				this.loadUserSubscriptions();
		});
	}

	onUserChange() {
		this.loadUserSubscriptions();
	}

	loadUserSubscriptions() {
		this.resetPeriods();

		if (!this.selectedUserId)
			return;

		this.mailerSubscriptionService.getSubscriptions(this.siteId, this.selectedUserId).subscribe((res: any) => {
			const savedPeriodsByReport = new Map<number, number[]>();
			for (const row of (res || [])) {
				if (!savedPeriodsByReport.has(row.report))
					savedPeriodsByReport.set(row.report, []);

				savedPeriodsByReport.get(row.report).push(row.period);
			}

			for (const family of this.reportRows) {
				for (const report of family.reports) {
					const savedPeriods = savedPeriodsByReport.get(report.code);
					report.periods = savedPeriods?.length ? savedPeriods : [NONE_PERIOD];
				}
			}
		});
	}

	resetPeriods() {
		for (const family of this.reportRows) {
			for (const report of family.reports) {
				report.periods = [NONE_PERIOD];
			}
		}
	}

	// None is mutually exclusive with Weekly/Monthly: picking None clears any real periods,
	// picking a real period (while None is set) drops None. An empty selection (e.g. removing the
	// last real period's chip) is shown as None rather than a blank placeholder.
	onPeriodsChange(report: { periods: number[] }, newSelection: number[]) {
		newSelection = newSelection || [];
		const noneJustAdded = newSelection.includes(NONE_PERIOD) && !report.periods.includes(NONE_PERIOD);

		const realPeriods = noneJustAdded ? [] : newSelection.filter(period => period !== NONE_PERIOD);
		report.periods = realPeriods.length ? realPeriods : [NONE_PERIOD];
	}

	submit() {
		// Send every eligible report (even with no periods) so the backend upserts the selected
		// periods and deletes the deselected ("None") ones.
		const subscriptions: { report: number, periods: number[] }[] = [];
		for (const family of this.reportRows) {
			for (const report of family.reports) {
				subscriptions.push({ report: report.code, periods: report.periods });
			}
		}

		this.saving = true;
		this.mailerSubscriptionService.saveSubscriptions(this.siteId, this.selectedUserId, subscriptions).subscribe({
			next: () => {
				this.saving = false;
				this.notificationMessage.setMessage('translate|g.successOperation', { type: 'success', clearOnXTimeNavigate: 1 });
				this.modal.hide();
			},
			error: () => {
				this.saving = false;
			}
		});
	}

	cancel() {
		this.modal.hide();
	}
}
